import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { CloseButton } from '@repo/common';
import { JOB_LABEL, cn, connectionLabel, isOnline, isStaffShown, presenceOf } from './model/derive';
import { KST, formatMemberHours } from './model/time';
import { useWorkboardCtx } from './model/useWorkboard';
import { StatusChip } from './primitives';

const initialsOf = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/** 작업 시간(KST) 한 줄 — "08:00–23:00 KST" */
export const hoursKst = (m, now) => `${formatMemberHours(m, KST, now)} KST`;

/**
 * 팀원 카드 내용 (읽기 전용). 이메일·시간대/국가·셀프체크·동의 항목은 보이지 않는다.
 * 이탈한 팀원은 이름·직무·Dropped 만.
 */
const CardBody = ({ member, now, onClose }) => {
  const { data } = useWorkboardCtx();
  const presence = presenceOf(member, now);
  const dropped = presence === 'dropped';
  // 상태 = 실제 접속, 작업 시간 밖이면 시간 아래에 Off hours now
  const online = isOnline(member, now, data?.viewerId);
  const tools = [...(member.tools ?? []), ...(member.stack ?? [])];
  // 프로필 사진 — 내 카드는 방금 바꾼 사진(me.photo)을 먼저
  const photo = member.id === data?.viewerId ? (data?.me?.photo ?? member.photo) : member.photo;
  return (
    <div className='flex flex-col gap-4'>
      {/* 머리 — 사진(없으면 이니셜) · 이름 · Leader/Staff · 직무 · 닫기 */}
      <div className='flex items-start gap-3'>
        {photo ? (
          <img
            src={photo}
            alt=''
            className='h-14 w-14 shrink-0 rounded-full object-cover object-center'
          />
        ) : (
          <span className='flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[16px] font-medium text-green-deep04'>
            {initialsOf(member.name)}
          </span>
        )}
        <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <div className='flex flex-wrap items-center gap-1.5'>
            <span className='text-[16px] font-medium text-text-primary'>{member.name}</span>
            {member.role === 'leader' && (
              <StatusChip chip={{ label: 'Leader', tone: 'newRequest' }} />
            )}
            {isStaffShown(member) && <StatusChip chip={{ label: 'Staff', tone: 'outline' }} />}
          </div>
          <span className='text-[13px] text-text-secondary'>
            {JOB_LABEL[member.job] ?? member.job}
            {dropped ? ' · Dropped' : ''}
          </span>
        </div>
        {onClose && (
          <div className='shrink-0'>
            <CloseButton onClick={onClose} />
          </div>
        )}
      </div>
      {!dropped && (
        <>
          <dl className='grid grid-cols-2 rounded-xl border border-green-deep03/[0.1] text-[13px]'>
            <div className='flex flex-col gap-1 border-r border-green-deep03/[0.1] px-3 py-2.5'>
              <dt className='text-text-secondary'>Status</dt>
              <dd className='flex items-center gap-1.5 text-text-primary'>
                <span
                  aria-hidden
                  className={cn(
                    'h-2 w-2 shrink-0 rounded-full',
                    online ? 'bg-functional-success03' : 'bg-grayscale-gray04',
                  )}
                />
                {connectionLabel(member, now, data?.viewerId)}
              </dd>
            </div>
            <div className='flex flex-col gap-1 px-3 py-2.5'>
              <dt className='text-text-secondary'>Working hours</dt>
              <dd className='flex flex-col text-text-primary'>
                {hoursKst(member, now)}
                {presence === 'offline' && (
                  <span className='text-[12px] text-text-secondary'>Off hours now</span>
                )}
              </dd>
            </div>
          </dl>
          {tools.length > 0 && (
            <div className='flex flex-col gap-2'>
              <div className='flex items-center justify-between text-[13px]'>
                <span className='font-medium text-text-primary'>Tools and stack</span>
                <span className='text-text-secondary'>Self-reported</span>
              </div>
              <div className='flex flex-wrap gap-1.5'>
                {tools.map((t) => (
                  <span
                    key={t}
                    className='inline-flex h-7 items-center rounded-full border border-green-deep03/[0.16] px-3 text-[12px] text-text-primary'
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * 이름을 누르면 팀원 카드 — 데스크톱은 팝오버, 모바일(640px 미만)은 하단 시트.
 * 팀장·없는 사람은 이름만 보여 준다.
 *   label   이름 대신 보일 글자 (멘션 "@이름")
 *   variant link(밑줄) · plain(댓글 작성자) · mention(본문 멘션)
 *   align   팝오버를 붙일 쪽 — 오른쪽 끝 이름은 right, 왼쪽에서 시작하면 left
 */
const MemberCard = ({ memberId, fallback = '–', label, variant = 'link', align = 'right' }) => {
  const { memberById, now } = useWorkboardCtx();
  const member = memberById.get(memberId);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const mobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    const onDown = (e) => !mobile && !ref.current?.contains(e.target) && setOpen(false);
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, mobile]);

  if (!member || member.role === 'npc') return <span>{label ?? member?.name ?? fallback}</span>;

  return (
    <span ref={ref} className='relative inline-flex'>
      <button
        type='button'
        aria-haspopup='dialog'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded text-left',
          variant === 'link' && 'underline underline-offset-2',
          variant === 'link' &&
            (open
              ? 'text-green-deep02 decoration-green-deep02'
              : 'decoration-grayscale-gray04 hover:decoration-text-primary'),
          variant === 'plain' && 'font-medium text-text-primary hover:underline underline-offset-2',
          variant === 'mention' && 'font-medium text-green-deep03 hover:underline underline-offset-2',
        )}
      >
        {label ?? member.name}
        {variant === 'link' && isStaffShown(member) && (
          <StatusChip chip={{ label: 'Staff', tone: 'outline' }} />
        )}
      </button>
      {open && !mobile && (
        <div
          role='dialog'
          aria-label={`${member.name} profile`}
          className={cn(
            'absolute top-7 z-30 w-[320px] rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-5 text-left font-normal whitespace-normal shadow-4',
            align === 'left' ? 'left-0' : 'right-0',
          )}
        >
          <CardBody member={member} now={now} onClose={() => setOpen(false)} />
        </div>
      )}
      {open &&
        mobile &&
        ReactDOM.createPortal(
          <div
            className='fixed inset-0 z-[1000] flex items-end bg-black/40'
            onMouseDown={() => setOpen(false)}
          >
            <div
              role='dialog'
              aria-label={`${member.name} profile`}
              onMouseDown={(e) => e.stopPropagation()}
              className='w-full rounded-t-2xl bg-grayscale-gray00 px-5 pb-8 pt-3'
            >
              <span
                aria-hidden
                className='mx-auto mb-4 block h-1 w-10 rounded-full bg-grayscale-gray03'
              />
              <CardBody member={member} now={now} onClose={() => setOpen(false)} />
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
};

export default MemberCard;
